/**
 * Endpoints #52-#55 — Telegram push delivery (SPEC `telegram`).
 *
 * Telegram is a **dumb surface**: it renders the strings the notification system already froze into
 * `payload_json` and adds no trigger logic of its own. Everything here is therefore configuration
 * plus the one read model the Settings tab renders — no rule, threshold or schedule lives on this
 * side of the contract.
 *
 * The bot token is stored in plaintext like every other secret (ADR-010, §10.3) and never leaves the
 * server: #52 ships the `maskSecret` spelling the Scrapers and Notion tabs already use.
 */
import { z } from 'zod';
/* ------------------------------------------------------------------ settings */
/**
 * The permanent-failure states worth surfacing as a banner. All three keep the binding — the user
 * fixes the bot and presses "Send test message", which clears it (#55).
 *
 * `conflict` is a second process polling `getUpdates` for the same token (dev + VPS): Telegram
 * answers `409` and the two would otherwise steal each other's updates forever.
 */
export const TELEGRAM_ERROR_KINDS = ['unauthorized', 'blocked', 'conflict'];
export function telegramSettingsDefault() {
    return {
        token: null,
        chatId: null,
        chatLabel: null,
        botUsername: null,
        botName: null,
        connectedAt: null,
        error: null,
    };
}
/** Frozen default for the §4.1 registry table; mutable copies come from the factory above. */
export const TELEGRAM_SETTINGS_DEFAULT = telegramSettingsDefault();
const TelegramStoredErrorSchema = z.object({
    kind: z.enum(TELEGRAM_ERROR_KINDS),
    message: z.string(),
    at: z.number().int(),
});
/**
 * Lenient decode of the stored blob: every field falls back rather than throwing, so a hand-edited
 * database can never crash a boot (same contract as the other §4.1 validators).
 */
const TelegramSettingsSchema = z.object({
    token: z.string().nullable().catch(null).default(null),
    chatId: z.number().int().nullable().catch(null).default(null),
    chatLabel: z.string().nullable().catch(null).default(null),
    botUsername: z.string().nullable().catch(null).default(null),
    botName: z.string().nullable().catch(null).default(null),
    connectedAt: z.number().int().nullable().catch(null).default(null),
    error: TelegramStoredErrorSchema.nullable().catch(null).default(null),
});
export function decodeTelegramSettings(value) {
    const parsed = TelegramSettingsSchema.safeParse(value);
    return parsed.success ? parsed.data : telegramSettingsDefault();
}
/** A binding exists when a token *and* a chat are stored — a pending handshake is neither. */
export function isTelegramConnected(settings) {
    return settings.token !== null && settings.token.length > 0 && settings.chatId !== null;
}
/* ------------------------------------------------------------------ requests */
/** #53 — a BotFather token, e.g. `123456:AAH…`. Validated against `getMe` before anything is kept. */
export const TelegramConnectBodySchema = z.strictObject({
    token: z.string().trim().min(1).max(200),
});
/* ------------------------------------------------------------------ constants */
/** How long a handshake code stays valid (SPEC § Connection handshake). */
export const TELEGRAM_CODE_TTL_MS = 10 * 60 * 1000;
/** Digits in the one-time code. */
export const TELEGRAM_CODE_DIGITS = 6;
//# sourceMappingURL=telegram.js.map